import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { EventEmitter, first, selectMany, sum } from "nate-react-api-helpers";
import { TablePersonalizeContext } from "./TablePersonalize";
import { TableSelectionContext } from "./Table";
import { useQueryParam } from "../../pages/project/quote/pricing/QueryParam";
import { useSyncedRef } from "../SyncedRef";
import { jsx as _jsx } from "react/jsx-runtime";
export const FilterContext = /*#__PURE__*/ createContext({ valueRef: {}, sortedRef: { current: [] }, onClear: new EventEmitter(), onFilter: new EventEmitter(), srcData: new EventEmitter(), // per-column filter text persisted in the URL query string so it survives
    // navigate-away-then-back (browser history). columnFilterInit seeds a cell's
    // initial value; setColumnFilter writes the latest value back (debounced).
    columnFilterInit: {}, setColumnFilter: (columnName, value) => { } });
export function buildFilterLink(input) { let p = new URLSearchParams(); p.append(input.tableName, JSON.stringify(input.filter)); return p.toString(); }
export function FilterProvider(props) {
    const onClear = useMemo(() => new EventEmitter(), []);
    const onFilter = useMemo(() => new EventEmitter(), []);
    const [filter, setFilter] = useState([]);
    const { sortBy, customSort, multiSortBy } = useContext(TablePersonalizeContext);
    const value = useRef({});
    const searchParamsFilterInit = useRef(false);
    const [searchParams, setSearchParams] = useQueryParam(props.tableName, ""); // auto clear search params if filters are changed
    useEffect(() => { if (!searchParamsFilterInit.current)
        return; setSearchParams(""); }, [filter, props.tableName, setSearchParams]);
    useEffect(() => { const sub = onClear.subscribe(() => setSearchParams("")); return () => sub.cancel(); }, [onClear, setSearchParams]); // per-column filter text, persisted in the URL under "<tableName>~cf" as a
    // JSON {columnName: text} map. Kept in browser history via replace-semantics
    // so a keystroke doesn't flood the back button.
    const [columnFilterParam, setColumnFilterParam] = useQueryParam(props.tableName + "~cf", "", { replace: true });
    const columnFilterInit = useMemo(() => { if (!columnFilterParam)
        return {}; try {
        return JSON.parse(columnFilterParam);
    }
    catch (e) {
        return {};
    } }, [columnFilterParam]); // live map of current cell values; seeded once from the URL, then mutated as
    // cells change. Writes to the URL are debounced.
    const columnFilterMap = useRef({});
    const columnFilterSeeded = useRef(false);
    if (!columnFilterSeeded.current) {
        columnFilterMap.current = Object.assign({}, columnFilterInit);
        columnFilterSeeded.current = true;
    }
    const columnFilterWriteTimer = useRef(null);
    const columnFilterParamRef = useSyncedRef(columnFilterParam);
    const setColumnFilter = useCallback((columnName, value) => { const map = columnFilterMap.current; if (value) {
        map[columnName] = value;
    }
    else {
        delete map[columnName];
    } if (columnFilterWriteTimer.current)
        clearTimeout(columnFilterWriteTimer.current); columnFilterWriteTimer.current = setTimeout(() => { const keys = Object.keys(map); const next = keys.length > 0 ? JSON.stringify(map) : ""; if (next === columnFilterParamRef.current)
        return; setColumnFilterParam(next); }, 400); }, [setColumnFilterParam, columnFilterParamRef]); // a pending write must not navigate back to this page after the user has left it
    useEffect(() => { return () => clearTimeout(columnFilterWriteTimer.current); }, []);
    useEffect(() => { const sub = onClear.subscribe(() => { columnFilterMap.current = {}; if (columnFilterWriteTimer.current)
        clearTimeout(columnFilterWriteTimer.current); setColumnFilterParam(""); }); return () => sub.cancel(); }, [onClear, setColumnFilterParam]);
    const columnsRef = useSyncedRef(props.columns);
    useEffect(() => {
        if (!searchParams) {
            delete value.current.fromSearchParams;
            return;
        }
        let filter = JSON.parse(searchParams);
        const filterFx = input => {
            if (filter.type === "eq") {
                for (let i in filter.search) { // @ts-ignore
                    if (input[i] !== filter.search[i])
                        return false;
                }
            }
            else if (filter.type === "contains") {
                for (let i in filter.search) { // @ts-ignore
                    if (filter.search[i].indexOf(input[i]) === -1)
                        return false;
                }
            }
            return true;
        };
        value.current = { fromSearchParams: filterFx };
        setFilter([filterFx]);
        setTimeout(() => { searchParamsFilterInit.current = true; }, 100);
    }, [props.tableName, searchParams, columnsRef]);
    const data = props.data;
    const srcData = useMemo(() => new EventEmitter(), []);
    useEffect(() => { srcData.emit(selectMany(data, d => d.rows)); }, [srcData, data]);
    const originalSortOrder = useRef(data);
    useEffect(() => { originalSortOrder.current = data.map(d => ({ key: d.key, rows: d.rows.slice(0) })); }, [data]);
    let sortFx = useSortFx({ columns: props.columns, sortBy: sortBy, multiSortBy: multiSortBy, customSort: customSort });
    const sorted = useMemo(() => { const findIndex = id => { let sum = 0; for (let i = 0; i < originalSortOrder.current.length; i++) {
        const g = originalSortOrder.current[i];
        const index = g.rows.findIndex(r => r.id === id);
        if (index === -1) {
            sum += g.rows.length;
            continue;
        }
        return sum + index;
    } return -1; }; const sortFx2 = (a, b) => { const aIndex = findIndex(a.id); const bIndex = findIndex(b.id); return aIndex - bIndex; }; let sorter = sortFx || sortFx2; return data.map(v => { v.rows = v.rows.slice(0); v.rows.sort(sorter); return v; }); }, [sortFx, data]);
    const sortedRef = useSyncedRef(sorted);
    const groupFilter = props.groupFilter;
    const filteredAndSorted = useMemo(() => { let dt = sorted.filter(v => groupFilter(v.key)); if (filter.length > 0) {
        return dt.map(grp => { return Object.assign({}, grp, { rows: grp.rows.filter(d => { for (let i = 0; i < filter.length; i++) {
                if (!filter[i](d))
                    return false;
            } return true; }) }); });
    } return dt; }, [sorted, filter, groupFilter]);
    useEffect(() => { onFilter.emit({ total: sum(props.data.map(d => d.rows.length)), visible: sum(filteredAndSorted.map(d => d.rows.length)) }); }, [filteredAndSorted, props.data, onFilter]);
    const { onFilteredData } = useContext(TableSelectionContext);
    useEffect(() => { onFilteredData(props.tableName, selectMany(filteredAndSorted, d => d.rows)); }, [props.tableName, filteredAndSorted, onFilteredData]);
    const filterRef = useRef(filter);
    filterRef.current = filter;
    useEffect(() => { const int = setInterval(() => { const newValues = Object.values(value.current); if (newValues.length === filterRef.current.length) {
        const hasChange = filterRef.current.filter((v, index) => newValues[index] !== v).length > 0;
        if (!hasChange)
            return;
    } setFilter(newValues); }, 800); return () => clearInterval(int); }, []);
    const ctx = useMemo(() => ({ valueRef: value.current, sortedRef: sortedRef, onClear: onClear, onFilter: onFilter, srcData: srcData, columnFilterInit: columnFilterInit, setColumnFilter: setColumnFilter }), [value, onClear, onFilter, srcData, sortedRef, columnFilterInit, setColumnFilter]);
    return /*#__PURE__*/ _jsx(FilterContext.Provider, { value: ctx, children: /*#__PURE__*/ _jsx(FilterContextPrivate.Provider, { value: filteredAndSorted, children: props.children }) });
}
export const FilterContextPrivate = /*#__PURE__*/ createContext([]);
function useSortFx(input) { const { sortBy, customSort, multiSortBy, columns } = input; return useMemo(() => { if (customSort)
    return customSort; if (multiSortBy) {
    const each = multiSortBy.map(col => { const column = first(columns, c => c.name === col.column); const sortFx = column === null || column === void 0 ? void 0 : column.sort; if (sortFx && col.reverse) {
        return (a, b) => -1 * sortFx(a, b);
    } return sortFx; }).filter(v => !!v);
    return (a, b) => { for (let i = 0; i < each.length; i++) {
        const result = each[i](a, b);
        if (result !== 0)
            return result;
    } return 0; };
} if (sortBy) {
    const col = first(columns, c => c.name === sortBy.column);
    const sortFx = col === null || col === void 0 ? void 0 : col.sort;
    if (sortFx) {
        if (sortBy.reverse) {
            return (a, b) => -1 * sortFx(a, b);
        }
        return sortFx;
    }
} return undefined; }, [customSort, multiSortBy, sortBy, columns]); }
